import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, CircleCheck, CloudOff, DraftingCompass, FolderKanban, Info, ListChecks, Package, Plus, ReceiptText,
  RefreshCw, Stamp, Truck, Unlink, UserRound, UserRoundX,
} from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { PageHeader } from '@/components/ui/page-header';
import { StatCard } from '@/components/ui/stat-card';
import { Skeleton } from '@/components/ui/skeleton';
import { NeedsCard } from '@/components/entry/NeedsCard';
import { PipelineCard } from '@/components/entry/PipelineCard';
import { MorningBriefCard } from '@/components/entry/MorningBriefCard';
import { buildNeedsItems } from '@/components/entry/needs';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { formatDate, formatMoney, formatNumber } from '@/lib/format';
import { getErrorMessage } from '@/lib/errors';
import { readStored, storageKey, writeStored } from '@/lib/storage';
import { toneClass } from '@/lib/status';
import { cn } from '@/lib/utils';
import { canAccessInvoicing, canEdit, hasPermission } from '@/lib/permissions';
import AiPinnedCards from '@/components/documents-ai/AiPinnedCards';

/*
  Dashboard: the honest KPI strip (wave 1) that links to the work, then "Needs you today" — one ranked
  list of what is late, blocked or waiting (02 #3) — beside the Delivery pipeline (02 #4) and "Data to fix".
  Everyone / Mine (02 #5) narrows every number and the list to the projects where I am the Project Engineer
  or Senior Project Engineer (matched on the server) and keeps my choice on this device.
  Above the numbers, the Morning brief (02 #10, components/entry/MorningBriefCard) sums up what needs a decision
  (under Mine, the brief of my projects).
  Numbers come from GET /dashboard (backend/dashboard_summary.py). Sections the user's role cannot
  open come back as null and are not shown (an empty list still means "nothing to show").
  Phones (< 640px): a compact head ("2 Oct · 29 things need you" beside Everyone / Mine) and compact KPI tiles (one
  meta line, no legend), so the first "Needs you today" card is on the first screen.
*/

const SCOPE_KEY = storageKey('dashboard', 'scope');
const PHONE_QUERY = '(max-width: 639px)';
const plural = (count, one, many = `${one}s`) => (count === 1 ? one : many);
const shortDate = (value) => formatDate(value, { year: 'auto' });
const monthYear = (value) => formatDate(value).replace(/^\d{1,2}\s/, '');
/** Truncated meta text that still shows in full on hover. */
const Clip = ({ text }) => <span title={text}>{text}</span>;

function DashboardSkeleton() {
  return (
    <div className="space-y-5" data-testid="dashboard-loading">
      <div className="space-y-2">
        <Skeleton className="h-8 w-48 rounded-xl" />
        <Skeleton className="h-4 w-72 rounded-lg" />
      </div>
      <div className="grid grid-cols-2 gap-2.5 md:grid-cols-3 md:gap-3.5 xl:grid-cols-5">
        {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-[90px] w-full rounded-card sm:h-[132px]" />)}
      </div>
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_400px]">
        <Skeleton className="h-80 w-full rounded-card" />
        <Skeleton className="h-64 w-full rounded-card" />
      </div>
    </div>
  );
}

const Card = forwardRef(({ className, children, ...props }, ref) => (
  <section ref={ref} className={cn('min-w-0 overflow-hidden rounded-card border border-line bg-surface shadow-card', className)} {...props}>
    {children}
  </section>
));
Card.displayName = 'DashboardCard';

function CardHead({ title, count, countTone = 'gray', sub, right, id }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-line-2 px-4 py-3.5 sm:px-[18px]">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <h2 id={id} className="text-[15px] font-bold text-ink">{title}</h2>
          {count !== undefined && count !== null ? (
            <span className={cn('rounded-full px-2 py-px text-[12px] font-bold tabular-nums', toneClass(countTone), 'bg-[color:var(--tone-bg)] text-[color:var(--tone-ink)]')}>
              {count}
            </span>
          ) : null}
        </div>
        {sub ? <p className="mt-0.5 text-[12.5px] text-ink-3">{sub}</p> : null}
      </div>
      {right}
    </div>
  );
}

/** Active / on hold / completed split — Active projects KPI. Phones keep the bar and drop the legend. */
function ProjectSplit({ projects, legend = true }) {
  const total = Math.max(1, projects.total || 0);
  const parts = [
    { key: 'active', label: 'Active', value: projects.active || 0, fill: 'bg-status-green', dot: 'tone-green' },
    { key: 'on_hold', label: 'On hold', value: projects.on_hold || 0, fill: 'bg-status-amber', dot: 'tone-amber' },
    { key: 'completed', label: 'Completed', value: projects.completed || 0, fill: 'bg-[color:var(--navy-300)]', dot: 'tone-navy' },
  ];
  const hasSplit = projects.on_hold !== undefined || projects.completed !== undefined;
  if (!hasSplit) return null;
  return (
    <>
      <div
        className="flex h-1.5 gap-px overflow-hidden rounded-full bg-[color:var(--line)]"
        role="img"
        aria-label={parts.map((part) => `${part.value} ${part.label.toLowerCase()}`).join(', ')}
      >
        {parts.map((part) => (part.value ? (
          <span key={part.key} className={part.fill} style={{ width: `${(part.value / total) * 100}%` }} />
        ) : null))}
      </div>
      {legend ? (
        // Three entries side by side (02 mockup): the number with its dot, the label under it.
        <div className="flex justify-between gap-x-1.5 text-[11.5px] leading-tight text-ink-3" data-testid="project-split-legend">
          {parts.map((part) => (
            <span key={part.key} className="flex min-w-0 flex-col" title={`${part.value} ${part.label.toLowerCase()}`}>
              <span className="inline-flex items-center gap-1">
                <span
                  className={cn('ui-tone-dot h-1.5 w-1.5', part.dot)}
                  style={part.key === 'completed' ? { background: 'var(--navy-300)' } : undefined}
                  aria-hidden="true"
                />
                <b className="font-bold text-ink-2 tabular-nums">{part.value}</b>
              </span>
              {' '}
              <span className="truncate">{part.label}</span>
            </span>
          ))}
        </div>
      ) : null}
    </>
  );
}

const SCOPE_NOTE_ID = 'dashboard-kpi-scope-note';

function KpiStrip({ data, onShowApprovals, isPhone, mine = false, showFinance = true }) {
  const approvals = data.pending_approvals || [];
  const canSeeApprovals = Array.isArray(data.pending_approvals);
  const oldest = approvals.find((item) => item.age_days !== null && item.age_days !== undefined);
  const late = data.late_deliveries;
  const blocked = data.blocked_drawings;
  // invoice money only for roles with Invoicing (the server sends null to the others as well)
  const unpaid = showFinance ? data.unpaid_invoices : null;
  const upcomingCount = (data.upcoming_deliveries || []).length;

  // Phones: two compact tiles per row (02 #3: the first "Needs you today" card on the first screen) — no icon
  // tile, a smaller value, one meta line; the second meta line and the split legend are left to wider screens.
  // [&_.font-heading]: the value line keeps StatCard's own tight 1.05 leading (its text-[size] class currently
  // drops it, which made every value line 1.5× tall).
  const kpiClass = 'h-full gap-1 px-3.5 py-2.5 sm:gap-2 sm:px-[18px] sm:py-4 [&_.font-heading]:leading-[1.05] [&_.ui-icon-tile]:hidden sm:[&_.ui-icon-tile]:grid';
  const size = isPhone ? 'compact' : 'default';
  const blockedProjects = blocked ? (blocked.by_project || blocked.top_projects || []) : [];
  const topBlocked = blockedProjects[0];
  const voCount = Number(unpaid?.vo_count) || 0;
  // Under "Mine" the numbers count my projects. The projects list can open narrowed the same way
  // (?mine=1); the drawings, LPO and invoice lists cannot yet, so those cards say they open everyone's.
  const everyoneList = mine
    ? { title: 'Opens the full list for all projects', 'aria-describedby': SCOPE_NOTE_ID }
    : {};
  const cards = [
    <StatCard
      key="active"
      className={kpiClass}
      size={size}
      label="Active projects"
      icon={FolderKanban}
      tone="navy"
      value={formatNumber(data.projects?.active ?? 0, { decimals: 0 })}
      unit={`of ${formatNumber(data.projects?.total ?? 0, { decimals: 0 })}`}
      to={mine ? '/projects?status=Active&mine=1' : '/projects?status=Active'}
      data-testid="stat-active-projects"
    >
      <ProjectSplit projects={data.projects || {}} legend={!isPhone} />
    </StatCard>,
    canSeeApprovals ? (
      <StatCard
        key="approvals"
        className={cn(kpiClass, 'relative')}
        size={size}
        label="Awaiting approval"
        icon={Stamp}
        tone="amber"
        value={approvals.length}
        unit={plural(approvals.length, 'stage')}
        meta={approvals.length
          ? (oldest ? `Oldest ${formatNumber(oldest.age_days, { decimals: 0 })} days` : 'No dated activity yet')
          : 'Nothing waiting'}
        metaTone={approvals.length ? 'amber' : 'green'}
        onClick={onShowApprovals}
        aria-label={`Awaiting approval: ${approvals.length} ${plural(approvals.length, 'stage')}. Show the list.`}
        data-testid="stat-pending-approvals"
      >
        {oldest && !isPhone ? (
          <div className="truncate text-[12px] text-ink-3">
            <span title={`${oldest.project_name} · ${oldest.stage_name} · last update ${formatDate(oldest.last_update)}`}>
              {oldest.project_name} · since {monthYear(oldest.last_update)}
            </span>
          </div>
        ) : null}
        {approvals.length && !isPhone ? (
          // 02 mockup: "View 16 →" beside the value on hover / keyboard focus (the card's own name says it already).
          <span
            className="pointer-events-none absolute right-[18px] top-[60px] inline-flex items-center gap-1 text-[12.5px] font-semibold text-brand-ink opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
            aria-hidden="true"
            data-testid="stat-pending-approvals-view"
          >
            View {formatNumber(approvals.length, { decimals: 0 })}
            <ArrowRight className="h-3.5 w-3.5" />
          </span>
        ) : null}
      </StatCard>
    ) : null,
    blocked ? (
      <StatCard
        key="blocked"
        className={kpiClass}
        size={size}
        label="Blocked drawings"
        icon={DraftingCompass}
        tone="red"
        value={formatNumber(blocked.count, { decimals: 0 })}
        unit={`of ${formatNumber(blocked.total, { decimals: 0 })}`}
        progress={isPhone ? undefined : (blocked.total ? (blocked.count / blocked.total) * 100 : 0)}
        progressTone="red"
        progressLabel="Share of shop drawings blocked"
        // The project with the most, then "+N more" — two long project names never fit one line.
        meta={topBlocked ? (
          <span title={blockedProjects.slice(0, 5).map((item) => `${item.project_name} ${item.count}`).join(' · ')}>
            {topBlocked.project_name} {formatNumber(topBlocked.count, { decimals: 0 })}
            {blockedProjects.length > 1 ? ` · +${blockedProjects.length - 1}${isPhone ? '' : ' more'}` : ''}
          </span>
        ) : 'Nothing blocked'}
        to="/shop-drawings?tab=blocked"
        {...everyoneList}
        data-testid="stat-blocked-drawings"
      />
    ) : null,
    late ? (
      <StatCard
        key="late"
        className={kpiClass}
        size={size}
        label="Late deliveries"
        icon={Truck}
        tone="blue"
        value={late.count}
        unit={plural(late.count, 'LPO')}
        meta={late.count
          ? `${formatMoney(late.total_value, { currency: late.currency || 'AED', decimals: 0 })} · ${formatNumber(late.min_days_late, { decimals: 0 })}+ days late`
          : 'Nothing late'}
        metaTone={late.count ? 'red' : 'green'}
        to="/procurement?filter=late"
        {...everyoneList}
        data-testid="stat-late-deliveries"
      >
        {!isPhone ? (
          <div className="truncate text-[12px] text-ink-3">
            <Clip
              text={late.count
                ? [...new Set(late.items.map((item) => item.supplier).filter(Boolean))].join(' · ')
                : `${upcomingCount} due in the next 30 days`}
            />
          </div>
        ) : null}
      </StatCard>
    ) : null,
    unpaid ? (
      <StatCard
        key="unpaid"
        className={kpiClass}
        size={size}
        label="Unpaid invoices"
        icon={ReceiptText}
        tone="violet"
        prefix="AED"
        value={formatNumber(unpaid.total_amount, { decimals: 0 })}
        // The amount is the base invoices (the Invoicing page's own KPI); the VO invoices it also lists as awaiting
        // payment are counted beside it, so the card matches the page it opens.
        meta={unpaid.count || voCount ? (
          <span
            title={voCount
              ? `${unpaid.count} base ${plural(unpaid.count, 'invoice')} (AED ${formatNumber(unpaid.total_amount, { decimals: 0 })}) and ${voCount} VO ${plural(voCount, 'invoice')} (AED ${formatNumber(unpaid.vo_total_amount, { decimals: 0 })}) awaiting payment`
              : undefined}
          >
            {voCount
              ? `${unpaid.count} base ${plural(unpaid.count, 'invoice')} · +${voCount} VO`
              : `${unpaid.count} submitted`}
          </span>
        ) : 'Nothing outstanding'}
        to="/invoicing?status=Submitted"
        {...everyoneList}
        data-testid="stat-unpaid-invoices"
      >
        {unpaid.oldest && !isPhone ? (
          <div className="truncate text-[12px] text-ink-3">
            <span title={[unpaid.oldest.project_name, unpaid.oldest.invoice_type, formatMoney(unpaid.oldest.amount, { decimals: 0 }), formatDate(unpaid.oldest.date)].filter(Boolean).join(' · ')}>
              {[`Oldest ${shortDate(unpaid.oldest.date)}`, unpaid.oldest.project_name].filter(Boolean).join(' · ')}
            </span>
          </div>
        ) : null}
      </StatCard>
    ) : null,
  ].filter(Boolean);
  // Phones show 2 × 2 tiles: late deliveries already lead the "Needs you today" list below.
  const shown = isPhone && cards.length > 4 ? cards.filter((card) => card.key !== 'late') : cards;
  const NOTE_NAMES = { blocked: 'blocked drawings', late: 'late deliveries', unpaid: 'unpaid invoices' };
  const everyoneCards = shown.map((card) => NOTE_NAMES[card.key]).filter(Boolean);

  return (
    <section aria-label="Key numbers" data-testid="dashboard-kpis">
      <div className="grid grid-cols-2 gap-2.5 md:grid-cols-3 md:gap-3.5 xl:grid-cols-5">
        {shown.map((card, index) => (
          <div
            key={card.key}
            className={cn('min-w-0', shown.length % 2 === 1 && index === shown.length - 1 && 'col-span-2 md:col-span-1')}
          >
            {card}
          </div>
        ))}
      </div>
      {mine && everyoneCards.length ? (
        <p id={SCOPE_NOTE_ID} className="mt-2 flex items-start gap-1.5 text-[12px] leading-snug text-ink-3" data-testid="dashboard-kpi-scope-note">
          <Info className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>
            Numbers count your projects. {joinList(everyoneCards).replace(/^./, (c) => c.toUpperCase())} open the full list for all projects.
          </span>
        </p>
      ) : null}
    </section>
  );
}

/** ['a', 'b', 'c'] → 'a, b and c'. */
function joinList(items) {
  if (items.length < 2) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

function DataToFixCard({ quality, allProjects = false }) {
  if (!quality) return null;
  const catalog = quality.catalog_without_min_level;
  const rows = [
    quality.projects_without_engineer !== null && quality.projects_without_engineer !== undefined && {
      key: 'engineer',
      count: quality.projects_without_engineer,
      icon: UserRoundX,
      tone: 'amber',
      text: <>{quality.projects_without_engineer} active {plural(quality.projects_without_engineer, 'project has', 'projects have')} no <abbr title="Project Engineer" className="no-underline">PE</abbr></>,
      sub: 'Nobody owns their stages and dates',
      action: 'Assign',
      to: '/projects?status=Active&engineer=none',
    },
    catalog && {
      key: 'min-level',
      count: catalog.missing,
      icon: Package,
      tone: 'red',
      text: `${formatNumber(catalog.missing, { decimals: 0 })} of ${formatNumber(catalog.total, { decimals: 0 })} items have no min stock`,
      sub: 'Low-stock alerts can’t fire',
      action: 'Set levels',
      to: '/inventory?tab=catalog&filter=no-min-level',
    },
    quality.lpos_with_missing_project !== null && quality.lpos_with_missing_project !== undefined && {
      key: 'lpo-project',
      count: quality.lpos_with_missing_project,
      icon: Unlink,
      tone: 'navy',
      text: `${quality.lpos_with_missing_project} ${plural(quality.lpos_with_missing_project, 'LPO points', 'LPOs point')} to deleted projects`,
      sub: 'Deliveries can’t be traced to a site',
      action: 'Relink',
      to: '/procurement?filter=unlinked',
    },
    quality.lpos_without_project !== null && quality.lpos_without_project !== undefined && {
      key: 'lpo-no-project',
      count: quality.lpos_without_project,
      icon: Unlink,
      tone: 'amber',
      text: `${quality.lpos_without_project} ${plural(quality.lpos_without_project, 'LPO has', 'LPOs have')} no project`,
      sub: 'Their deliveries don’t count towards any site',
      action: 'Assign',
      to: '/procurement?filter=unlinked',
    },
    // Wave 2: open tasks with no project, person or date wait in Tasks › "Needs details" (the old "Untitled
    // task"s are among them), so the row counts those and opens that view. Older servers only send
    // untitled_tasks → the row below.
    typeof quality.tasks_needing_details === 'number' ? {
      key: 'task-details',
      count: quality.tasks_needing_details,
      icon: ListChecks,
      tone: 'gray',
      text: `${quality.tasks_needing_details} open ${plural(quality.tasks_needing_details, 'task has', 'tasks have')} no project, person or date`,
      sub: 'Give them details or close them',
      action: 'Clean up',
      to: '/tasks?focus=details',
    } : quality.untitled_tasks !== null && quality.untitled_tasks !== undefined && {
      key: 'untitled',
      count: quality.untitled_tasks,
      icon: ListChecks,
      tone: 'gray',
      text: `${quality.untitled_tasks} open ${plural(quality.untitled_tasks, 'task is', 'tasks are')} named “Untitled task”`,
      sub: 'Give them a real name or close them',
      action: 'Clean up',
      to: `/tasks?search=${encodeURIComponent('Untitled task')}`,
    },
  ].filter(Boolean);
  if (!rows.length) return null;
  const open = rows.filter((row) => row.count > 0);

  return (
    <Card aria-labelledby="data-to-fix-title" data-testid="data-to-fix-card">
      <CardHead
        id="data-to-fix-title"
        title="Data to fix"
        count={open.length}
        countTone={open.length ? 'amber' : 'green'}
        sub={allProjects ? "Clean data = alerts you can trust · all projects" : "Clean data = alerts you can trust"}
      />
      {open.length === 0 ? (
        <EmptyState layout="inline" tone="green" icon={CircleCheck} title="All clear" text="Every alert on this page has the data it needs." titleAs="h3" className="px-[18px] py-5" />
      ) : (
        <ul className="divide-y divide-[color:var(--line-2)]">
          {open.map((row) => {
            const Icon = row.icon;
            return (
              <li key={row.key} className="flex items-center gap-3 px-4 py-3 sm:px-[18px]" data-testid={`data-to-fix-${row.key}`}>
                <span className={cn('ui-icon-tile h-7 w-7 shrink-0 rounded-lg', toneClass(row.tone))} aria-hidden="true">
                  <Icon className="h-[15px] w-[15px]" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-semibold leading-snug text-ink">{row.text}</p>
                  <p className="text-[12px] leading-snug text-ink-3">{row.sub}</p>
                </div>
                <Link
                  to={row.to}
                  className="inline-flex min-h-[44px] shrink-0 items-center rounded text-[13px] font-semibold text-brand-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:min-h-0"
                >
                  {row.action}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function LowStockCard({ items }) {
  if (!items?.length) return null;
  return (
    <Card aria-labelledby="low-stock-title" data-testid="low-stock-card">
      <CardHead id="low-stock-title" title="Low stock" count={items.length} countTone="red" sub="At or below the item’s min stock level" />
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead className="bg-surface-2 text-left text-[12px] font-semibold text-ink-3">
            <tr>
              <th className="px-[18px] py-2 font-semibold">Part no.</th>
              <th className="px-3 py-2 font-semibold">Description</th>
              <th className="px-3 py-2 text-right font-semibold">On hand</th>
              <th className="px-[18px] py-2 text-right font-semibold">Min level</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[color:var(--line-2)]">
            {items.map((item) => (
              <tr key={item.part_number}>
                <td className="px-[18px] py-2.5 font-mono text-[12px] text-ink">{item.part_number}</td>
                <td className="px-3 py-2.5 text-ink-2">{item.description || '—'}</td>
                <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-status-red-ink">{formatNumber(item.on_hand_qty, { decimals: 0 })}</td>
                <td className="px-[18px] py-2.5 text-right tabular-nums text-ink-3">{formatNumber(item.min_stock_level, { decimals: 0 })}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}


const APPROVER_ROLES = new Set(['Admin', 'Super Admin']);
const APPROVALS_SHOWN = 5;

/**
  LPOs awaiting your approval (22 #9) — management approves drafts over the limit from here, the bell or the phone.
  Admin / Super Admin with Procurement only (the server decides who may approve: `can_approve`); hidden when none
  wait. Each row opens the draft in Procurement › Orders › Drafts & approvals (?tab=orders&lpo=<number>).
*/
function LpoApprovalsCard({ user }) {
  const show = APPROVER_ROLES.has(String(user?.role || '')) && hasPermission(user, 'procurement');
  const [state, setState] = useState({ items: [], count: 0, canApprove: false });
  useEffect(() => {
    if (!show) return undefined;
    let cancelled = false;
    api.get('/procurement/lpo-approvals')
      .then((response) => {
        if (cancelled) return;
        const items = Array.isArray(response.data?.items) ? response.data.items : [];
        setState({ items, count: Number(response.data?.count) || items.length, canApprove: Boolean(response.data?.can_approve) });
      })
      .catch(() => {}); // the Procurement page and the bell still show them
    return () => { cancelled = true; };
  }, [show]);
  if (!show || !state.canApprove || !state.items.length) return null;
  const shown = state.items.slice(0, APPROVALS_SHOWN);
  return (
    <Card aria-labelledby="lpo-approvals-title" data-testid="dashboard-lpo-approvals">
      <CardHead
        id="lpo-approvals-title"
        title="LPOs awaiting your approval"
        count={state.count}
        countTone="amber"
        sub="Drafts over the approval limit, sent by Procurement"
        right={(
          <Link to="/procurement?tab=orders" className="shrink-0 text-[12.5px] font-semibold text-brand-ink hover:underline">
            All drafts
          </Link>
        )}
      />
      <ul className="divide-y divide-[color:var(--line-2)]">
        {shown.map((lpo) => {
          const submitted = lpo.approval?.submitted_at;
          return (
            <li key={lpo.id}>
              <Link
                to={`/procurement?tab=orders&lpo=${encodeURIComponent(lpo.lpo_number || '')}`}
                className="flex items-center gap-3 px-4 py-2.5 hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:outline-none sm:px-[18px]"
                data-testid={`dashboard-lpo-approval-${lpo.id}`}
              >
                <Stamp className="h-4 w-4 shrink-0 text-status-amber-ink" aria-hidden="true" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-semibold text-ink">
                    {lpo.lpo_number || 'Draft LPO'} · {lpo.supplier || 'No supplier'}
                  </span>
                  <span className="block truncate text-[12px] text-ink-3">
                    {[lpo.project_name, submitted ? `sent ${formatDate(submitted)}${lpo.approval?.submitted_by ? ` by ${lpo.approval.submitted_by}` : ''}` : '']
                      .filter(Boolean).join(' · ') || 'Waiting for approval'}
                  </span>
                </span>
                <span className="shrink-0 text-[13px] font-semibold tabular-nums text-ink">
                  {formatMoney(lpo.approval_amount, { currency: lpo.currency || 'AED', decimals: 0 })}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
      {state.count > shown.length ? (
        <p className="border-t border-line-2 px-4 py-2 text-[12px] text-ink-3 sm:px-[18px]">+{state.count - shown.length} more in Procurement</p>
      ) : null}
    </Card>
  );
}

/** Everyone | Mine (02 #5). "Mine" = projects where I am the Project Engineer or Senior Project Engineer. */
function ScopeSwitch({ value, onChange, busy }) {
  const options = [
    { key: 'everyone', label: 'Everyone' },
    { key: 'mine', label: 'Mine', icon: UserRound, title: 'Only projects where you are the Project Engineer or Senior Project Engineer, and tasks assigned to you' },
  ];
  return (
    <div
      role="group"
      aria-label="Whose work to show"
      aria-busy={busy || undefined}
      className="inline-flex shrink-0 gap-0.5 rounded-[11px] bg-[color:var(--status-gray-bg)] p-[3px]"
      data-testid="dashboard-scope"
    >
      {options.map((option) => {
        const active = option.key === value;
        const Icon = option.icon;
        return (
          <button
            key={option.key}
            type="button"
            aria-pressed={active}
            title={option.title}
            onClick={() => onChange(option.key)}
            data-testid={`dashboard-scope-${option.key}`}
            className={cn(
              'inline-flex h-[34px] shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-3 text-[13px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:h-[30px]',
              active ? 'bg-surface text-ink shadow-card' : 'text-ink-2 hover:text-ink'
            )}
          >
            {Icon ? <Icon className="h-3.5 w-3.5" aria-hidden="true" /> : null}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

const readScope = () => (readStored(SCOPE_KEY, 'everyone') === 'mine' ? 'mine' : 'everyone');

export default function DashboardPage() {
  const { user } = useAuth() || {};
  const isPhone = useMediaQuery(PHONE_QUERY);
  // Same rule as the Projects page's New project: canEdit — no shortcut for a role whose Projects level is View
  // (Admin › Access) or for Viewer accounts (BUG-002). The server enforces it on POST /projects.
  const canCreateProject = canEdit(user, 'projects');
  const [scope, setScope] = useState(readScope);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [category, setCategory] = useState('all');
  const needsRef = useRef(null);
  const requestRef = useRef(0);

  const load = useCallback(async (nextScope) => {
    const request = ++requestRef.current;
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/dashboard', nextScope === 'mine' ? { params: { scope: 'mine' } } : undefined);
      if (request === requestRef.current) setData(res.data);
    } catch (err) {
      if (request === requestRef.current) setError(getErrorMessage(err, 'Could not load the dashboard'));
    } finally {
      if (request === requestRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(scope);
  }, [load, scope]);

  const changeScope = useCallback((next) => {
    setScope(next);
    writeStored(SCOPE_KEY, next);
  }, []);

  // Owner rule 13 Oct 2026: invoice and VO amounts only for roles with Invoicing.
  const showFinance = canAccessInvoicing(user);
  const items = useMemo(() => buildNeedsItems(data, { showFinance }), [data, showFinance]);

  const showApprovals = useCallback(() => {
    setCategory('approvals');
    requestAnimationFrame(() => {
      needsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      needsRef.current?.focus?.({ preventScroll: true });
    });
  }, []);

  if (loading && !data) return <DashboardSkeleton />;
  if (!data) {
    return (
      <div className="rounded-card border border-line bg-surface shadow-card" data-testid="dashboard-error">
        <EmptyState
          icon={CloudOff}
          tone="red"
          title="Couldn’t load the dashboard"
          text={error}
          titleAs="h1"
          action={<Button variant="outline" onClick={() => load(scope)}><RefreshCw />Try again</Button>}
        />
      </div>
    );
  }

  const mine = data.scope === 'mine';
  const active = data.projects?.active ?? 0;
  const today = formatDate(data.as_of || new Date(), { weekday: true });
  const things = `${formatNumber(items.length, { decimals: 0 })} ${plural(items.length, 'thing needs', 'things need')} attention`;
  const across = mine
    ? `across your ${formatNumber(active, { decimals: 0 })} active ${plural(active, 'project')}`
    : `across ${formatNumber(active, { decimals: 0 })} active ${plural(active, 'project')}`;
  const noMine = mine && data.mine && data.mine.project_count === 0;
  const needYou = `${formatNumber(items.length, { decimals: 0 })} ${plural(items.length, 'thing needs', 'things need')} you`;

  return (
    <div data-testid="dashboard-page" className="space-y-4 sm:space-y-5">
      {isPhone ? (
        // Phones (02 #3, mobile mockup): one compact head — the date and how much needs me, beside Everyone / Mine.
        <div className="flex items-center justify-between gap-3" data-testid="dashboard-phone-head">
          <div className="min-w-0">
            <h1 className="font-heading text-[20px] font-black leading-[1.15] tracking-[-0.02em] text-ink">Dashboard</h1>
            <p className="mt-0.5 truncate text-[13px] text-ink-3" title={`${today} — ${things} ${across}.`}>
              {shortDate(data.as_of || new Date())} · <b className="font-semibold text-ink">{needYou}</b>
            </p>
          </div>
          <ScopeSwitch value={scope} onChange={changeScope} busy={loading} />
        </div>
      ) : (
        <PageHeader
          title="Dashboard"
          subtitle={`${today} — ${things} ${across}.`}
          actions={(
            <>
              <ScopeSwitch value={scope} onChange={changeScope} busy={loading} />
              {canCreateProject ? (
                // 02 mockup: the page's one navy action. /projects?new=1 opens the New project panel there.
                <Button asChild data-testid="dashboard-new-project">
                  <Link to="/projects?new=1"><Plus aria-hidden="true" />New project</Link>
                </Button>
              ) : null}
            </>
          )}
        />
      )}

      {error ? (
        <p className="rounded-control border border-[color:var(--danger-border)] bg-status-red-bg px-3.5 py-2.5 text-[13px] text-status-red-ink" role="alert">
          {error} <button type="button" className="font-semibold underline" onClick={() => load(scope)}>Try again</button>
        </p>
      ) : null}

      {noMine ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-control border border-[color:var(--brand-soft-border)] bg-brand-soft px-3.5 py-3 text-[13px] text-ink-2" data-testid="dashboard-mine-empty">
          <Info className="h-4 w-4 shrink-0 text-brand-ink" aria-hidden="true" />
          <p className="min-w-0 flex-1">
            <b className="font-bold text-ink">No projects list you as Project Engineer or Senior PE.</b>{' '}
            “Mine” matches those fields to your name{user?.name ? ` (${user.name})` : ''}. Tasks assigned to you still show.
          </p>
          <Button type="button" variant="outline" size="sm" onClick={() => changeScope('everyone')}>Show everyone</Button>
        </div>
      ) : null}

      {/* 02 #10: the 3-4 things that need a decision today (same brief as the AI page; loads on its own).
          Under Mine it is the brief of my projects, so it agrees with the numbers below. */}
      <MorningBriefCard isPhone={isPhone} scope={scope} />

      <div className={cn('space-y-4 transition-opacity sm:space-y-5', loading && 'opacity-60')} aria-busy={loading || undefined}>
        <KpiStrip data={data} onShowApprovals={showApprovals} isPhone={isPhone} mine={mine} showFinance={showFinance} />

        <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_360px] xl:grid-cols-[minmax(0,1fr)_400px]">
          <NeedsCard
            ref={needsRef}
            items={items}
            data={data}
            category={category}
            onCategoryChange={setCategory}
            isPhone={isPhone}
          />
          <div className="flex min-w-0 flex-col gap-4">
            <div className="order-2 lg:order-1">
              <PipelineCard pipeline={data.pipeline} isPhone={isPhone} />
            </div>
            <div className="order-1 space-y-4 lg:order-2">
              <LpoApprovalsCard user={user} />
              <DataToFixCard quality={data.data_quality} allProjects={mine} />
              <LowStockCard items={data.low_stock_alerts} />
            </div>
          </div>
        </div>
        {/* 25 #10: questions pinned on the AI page, answered again every morning (shown only when there are any). */}
        {hasPermission(user, 'ai') ? <AiPinnedCards /> : null}
      </div>
    </div>
  );
}
